import i18n from "../../i18n";

import { Typography } from "@mui/material";

import {
  TEXT_FIELD_TYPE,
  AUTOCOMPLETE_FIELD_TYPE,
  NUMERIC_FIELD_TYPE,
  DATETIME_TO_DATE_RANGE_FIELD_TYPE,
} from "../../helpers/constants";

import {
  getStyleForSLACell,
  getStyleForTicketResponseTimeCell,
} from "../../helpers/styles";

import WindowService from "../../services/windowService";
import DateService from "../../services/dateService";

import WarningAlertIcon from "../../components/other/WarningAlertIcon";
import PrefixSuffixFieldTableWrapper from "../../components/wrapper/PrefixSuffixFieldTableWrapper";

export const TICKET_SLA_TABLE_NAME = "ticket";

export const TICKET_SLA_TABLE_FIELDS_CONFIG = [
  {
    name: "ticket_nr",
    getValueCallback: (rowData) => rowData.ticket_nr,
    label: i18n.t("table.ticket.ticket_nr"),
    filterType: NUMERIC_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    allowChangeVisibility: false,
    sortable: true,
  },
  {
    name: "ticket_name",
    getValueCallback: (rowData) => rowData.ticket_name,
    label: i18n.t("table.ticket.ticket_name"),
    filterType: TEXT_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
    allowChangeVisibility: false,
  },
  {
    name: "ticket_category",
    getValueCallback: (rowData) => rowData.ticket_category,
    label: i18n.t("table.ticket.ticket_category"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData.value.ticket_category,
      isObjectOption: true,
      optionLabelKey: "tc_name",
      addNewValue: false,
      multiple: true,
    }),
  },
  {
    name: "created",
    getValueCallback: (rowData) =>
      DateService.convertDatetimeFromBackendToFormatYYYYMMDD_hhmmss(
        rowData.created
      ),
    label: i18n.t("table.ticket.created"),
    filterType: DATETIME_TO_DATE_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      style: { minWidth: "100px" },
    }),
    sortable: true,
  },
  {
    name: "ticket_date_closeALL_real",
    getValueCallback: (rowData) =>
      DateService.convertDatetimeFromBackendToFormatYYYYMMDD(
        rowData.ticket_date_closeALL_real
      ),
    label: i18n.t("table.ticket.closed"),
    filterType: DATETIME_TO_DATE_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      style: { minWidth: "100px" },
    }),
    sortable: true,
  },
  {
    name: "ticket_status",
    getValueCallback: (rowData) => rowData.ticket_status,
    label: i18n.t("table.ticket.ticket_status"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData.value.ticket_status,
      isObjectOption: true,
      optionLabelKey: "status_name",
      addNewValue: false,
      multiple: true,
    }),
  },
  {
    name: "ticket_k",
    getValueCallback: (rowData) => rowData.ticket_k,
    label: i18n.t("table.ticket.ticket_k"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData.value.ticket_k,
      isObjectOption: true,
      optionLabelKey: "full_name",
      addNewValue: false,
      multiple: true,
    }),
  },
  {
    name: "ticket_owner_full_name",
    getValueCallback: (rowData) => rowData.ticket_owner,
    label: i18n.t("table.ticket.ticket_owner"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData.value.ticket_owner,
      isObjectOption: true,
      optionLabelKey: "full_name",
      optionValueKey: "full_name",
      addNewValue: false,
      multiple: true,
    }),
    other_export_field_name: "ticket_owner",
  },
  {
    name: "tenant",
    getValueCallback: (rowData) => rowData.tenant,
    label: i18n.t("table.ticket.tenant"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData.value.tenant,
      isObjectOption: true,
      optionLabelKey: "tenant_short_name",
      addNewValue: false,
      multiple: true,
    }),
  },
  {
    name: "objects_level_2_id",
    getValueCallback: (rowData, onClickMultipleLocationAlert) => {
      return rowData.is_multiple_locations ? (
        <PrefixSuffixFieldTableWrapper
          prefix={
            <WarningAlertIcon
              style={{ cursor: "pointer" }}
              onClick={(e) => onClickMultipleLocationAlert(e, rowData.id)}
            />
          }
        >
          {rowData.ticket_object}
        </PrefixSuffixFieldTableWrapper>
      ) : (
        rowData.ticket_object
      );
    },
    label: i18n.t("table.ticket.ticket_object"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData.value.ticket_object,
      isObjectOption: true,
      optionLabelKey: "object_name",
      addNewValue: false,
      multiple: true,
    }),
    other_export_field_name: "objects_level_2",
  },

  {
    name: "related_tickets",
    sortable: true,
    getValueCallback: (rowData) => {
      return rowData?.related_tickets ? (
        <div style={{ display: "flex" }}>
          {rowData.related_tickets.map((ticket) => (
            <Typography
              onClick={() =>
                WindowService.openInNewTab(`/ticket_details/${ticket.id}`)
              }
              style={{ margin: 0, color: "var(--primary)" }}
            >
              {ticket.ticket_nr}{" "}
            </Typography>
          ))}
        </div>
      ) : null;
    },
    label: i18n.t("table.ticket.related_tickets"),
    filterType: AUTOCOMPLETE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      options: filteringData.value.related_tickets,
      isObjectOption: true,
      optionLabelKey: "ticket_nr",
      addNewValue: false,
      multiple: true,
    }),
  },
  {
    name: "sla",
    getValueCallback: (rowData) => rowData.sla,
    sortable: true,
    label: i18n.t("table.ticket.sla"),
    filterType: NUMERIC_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
  },
  {
    name: "sla_lead_time",
    getValueCallback: (rowData) => rowData.sla_lead_time,
    sortable: true,
    label: i18n.t("table.ticket.sla_lead_time"),
    filterType: NUMERIC_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => undefined,
  },
  {
    name: "sla_delay_time",
    getValueCallback: (rowData) => rowData.sla_delay_time,
    cellStyle: (rowData) => getStyleForSLACell(rowData.sla_delay_time),
    sortable: true,
    label: i18n.t("table.ticket.sla_delay_time"),
    filterType: NUMERIC_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      allowNegative: true,
    }),
  },
  {
    name: "ticket_date_response",
    getValueCallback: (rowData) =>
      DateService.convertDatetimeFromBackendToFormatYYYYMMDD_hhmmss(
        rowData.ticket_date_response
      ),
    label: i18n.t("table.ticket.ticket_date_response"),
    filterType: DATETIME_TO_DATE_RANGE_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      style: { minWidth: "100px" },
    }),
    sortable: true,
  },
  {
    name: "ticket_response_time",
    getValueCallback: (rowData) => rowData.ticket_response_time,
    cellStyle: (rowData) =>
      getStyleForTicketResponseTimeCell(rowData.ticket_response_time),
    sortable: true,
    label: i18n.t("table.ticket.ticket_response_time"),
    filterType: NUMERIC_FIELD_TYPE,
    getFilterFieldConfigCallback: (filteringData) => ({
      allowNegative: true,
    }),
  },
];
